import { Modal } from 'react-bootstrap'
import isEmpty from 'lodash/isEmpty'
import { useMutation, useError, useToast } from '@ztechapps/common.hook'
import { toValidNumber } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { Modal as ModalCore, ModalDragHandle } from '@ztechapps/webui.modal'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { Form, Field, FormSaveCancelButtons } from '@ztechapps/webui.form'
import { TextInput, NumberInput } from '@ztechapps/webui.input'
import { GRANT_CREDITS_MUTATION } from './graphql'
import type { TopUpFormValues } from './validateTopUp'
import { validateTopUp } from './validateTopUp'

// Shared by every "grant AI credits" entry point — the admin-picks-any-user screen (QuickScore's
// /app/credits) and the self-service top-up (TotalMilk's AI Usage preference card) — so there is
// exactly one grant-credits implementation. `ownerId` is undefined until a target user is known;
// a caller whose flow requires a picked user first (rather than defaulting to "self") passes
// `disableWhenNoOwner` so the trigger stays disabled-but-visible instead of not rendering at all,
// keeping a stable button position while the picker is empty.
export type TopUpCreditsModalProps = {
  ownerId: string | undefined
  ownerName?: string
  currency?: string
  buttonText?: string
  disableWhenNoOwner?: boolean
  refetchQueries: string[]
}

export const TopUpCreditsModal = ({
  ownerId,
  ownerName,
  currency,
  buttonText = 'Add credits',
  disableWhenNoOwner = false,
  refetchQueries,
}: TopUpCreditsModalProps) => {
  const [executeGrant, , { error, loading }] = useMutation({
    query: GRANT_CREDITS_MUTATION,
    options: {
      mutationOptions: {
        // Apollo refetches ANY active query with this operation name using its CURRENT
        // variables — the caller's refetchQueries list re-fetches its own already-mounted
        // balance/usage queries, not some other owner's.
        refetchQueries,
      },
    },
  })
  useError(error)
  const { showSuccess } = useToast()

  const isTriggerDisabled = disableWhenNoOwner && !ownerId

  const trigger = ({ onClick }: ModalTriggerProps) => (
    <BusyButton
      icon="plus"
      variant="primary"
      text={buttonText}
      isBusy={loading}
      disabled={isTriggerDisabled}
      title={isTriggerDisabled ? 'Select a user first' : undefined}
      onClick={onClick}
    />
  )

  return (
    <ModalCore trigger={trigger} backdrop={true}>
      {({ toggleShowModal }) => (
        <>
          <ModalDragHandle>
            <Modal.Title>Top Up AI Credits{ownerName ? ` — ${ownerName}` : ''}</Modal.Title>
          </ModalDragHandle>

          <Modal.Body>
            <Form
              initialValues={{ credits: 0, reason: '' } as TopUpFormValues}
              validate={validateTopUp}
              onSubmit={async (values: TopUpFormValues) => {
                if (!ownerId) return
                // `currency` is omitted by callers that don't pass it — dropped by
                // JSON.stringify on an undefined value, so it never reaches the wire; see the
                // module comment on GRANT_CREDITS_MUTATION.
                const result = await executeGrant({
                  input: {
                    ownerId,
                    credits: toValidNumber(values.credits),
                    currency,
                    reason: values.reason,
                  },
                })
                if (isEmpty(result?.errors)) {
                  showSuccess({ message: 'Credits granted successfully' })
                  toggleShowModal()
                }
              }}
            >
              <Field name="credits" label="Amount" component={NumberInput} />
              <div className="mb-3">
                <Field name="reason" label="Reason" component={TextInput} />
              </div>
              <FormSaveCancelButtons onCancel={toggleShowModal} saveText="Grant" />
            </Form>
          </Modal.Body>
        </>
      )}
    </ModalCore>
  )
}
